import {
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
  type Ref,
} from 'react'
import {
  Alert,
  Autocomplete,
  Box,
  Checkbox,
  Button,
  FormControl,
  FormControlLabel,
  FormHelperText,
  InputLabel,
  LinearProgress,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  TextField,
  Typography,
} from '@mui/material'
import type { Theme } from '@mui/material/styles'
import type { GridSortModel } from '@mui/x-data-grid-premium'
import { useParams, useSearchParams } from 'react-router-dom'
import DataGridPremiumDemo from '@/components/Table/DataGrid'
import detailedReadingsColumns from '@/components/features/config/columns/reportGeneration/detailedReadings.column.config'
import {
  getFilterOptionsWithNames,
  type FilterOption,
} from '@/services/consumerService'
import { formatCodeName } from '@/utils/codeName'
import {
  getWaterTreeZonesList,
  type WaterTreeZoneListItem,
} from '@/services/dashboardService'
import {
  exportDetailedReadings,
  getDetailedReadings,
  type DetailedReadingsPage,
  type DetailedReadingsRequest,
} from '@/services/detailedReadingsService'
import { useAppTheme } from '@/theme/ThemeContext'
import FileIcon from '@assets/ReportGeneration/File.svg'
import AppDateRangePicker from '@/components/DatePickers/AppDateRangePicker'
import {
  formatDay,
  parseDay,
  type DateRangeValue,
} from '@/components/DatePickers/dateRange'
import {
  SOURCE_LABELS,
  datesReversed,
  formFromUrl,
  inputFor,
  isSource,
  nonNumbers,
  searchOf,
  type DetailedReadingsForm,
  type SourceInput,
} from './detailedReadingsForm'

export type DetailedReadingsHandle = { exportExcel: () => Promise<void> }

/** One grid view of a search: the request plus the 0-based grid page. */
type Query = { request: DetailedReadingsRequest; page: number; size: number }

type ReportState =
  | { status: 'idle' }
  | { status: 'loading'; query: Query; shown: DetailedReadingsPage | null }
  | { status: 'loaded'; query: Query; data: DetailedReadingsPage }
  | { status: 'error'; message: string }

type ExportState =
  | { status: 'idle' }
  | { status: 'running' }
  | { status: 'failed'; message: string }

type Options<T> =
  | { status: 'loading' }
  | { status: 'ready'; items: T[] }
  | { status: 'failed' }

const DEFAULT_SORT: Pick<DetailedReadingsRequest, 'sortBy' | 'sortOrder'> = {
  sortBy: 'meterNumber',
  sortOrder: 'ASC',
}
const PAGE_SIZE = 50

// The live report shares this page's path, and so its default settings key.
const COLUMN_SETTINGS_KEY = 'columnSettings_reportGeneration_detailedReadings'

const TEXT_SX = { fontFamily: 'Rubik, sans-serif', fontSize: 14 }

const searchButtonSx = (theme: Theme) => ({
  background:
    theme.palette.mode === 'dark' ? 'rgba(96, 165, 250, 0.15)' : '#E3F5FF',
  border: `1px solid ${theme.palette.divider}`,
  color: theme.palette.text.primary,
  fontWeight: 400,
  '&:hover': {
    background:
      theme.palette.mode === 'dark' ? 'rgba(96, 165, 250, 0.25)' : '#d6ecff',
  },
  alignSelf: 'center',
  flexShrink: 0,
})

const queryOf = (report: ReportState): Query | null =>
  report.status === 'loading' || report.status === 'loaded'
    ? report.query
    : null

const shownOf = (report: ReportState): DetailedReadingsPage | null => {
  if (report.status === 'loaded') return report.data
  if (report.status === 'loading') return report.shown
  return null
}

const useOptions = <T,>(load: () => Promise<T[]>): Options<T> => {
  const [options, setOptions] = useState<Options<T>>({ status: 'loading' })
  useEffect(() => {
    let live = true
    load().then(
      items => live && setOptions({ status: 'ready', items }),
      () => live && setOptions({ status: 'failed' })
    )
    return () => {
      live = false
    }
  }, [load])
  return options
}

const ListInput = ({
  input,
  onChange,
}: {
  input: Extract<SourceInput, { text: string }>
  onChange: (input: SourceInput) => void
}) => {
  const invalid = nonNumbers(input.text)
  const prompt =
    input.source === 'waterCards'
      ? 'הזן כרטיסי מים (מופרדים בפסיק או שורה חדשה)'
      : 'הזן מספרי מדים (מופרדים בפסיק או שורה חדשה)'
  return (
    <TextField
      multiline
      minRows={2}
      maxRows={4}
      placeholder={prompt}
      value={input.text}
      onChange={e => onChange({ ...input, text: e.target.value })}
      error={invalid.length > 0}
      helperText={
        invalid.length > 0 ? `ערך שאינו מספר: ${invalid.join(', ')}` : ''
      }
      inputProps={{
        'aria-label': prompt,
        'data-testid': 'detailed-readings-values',
      }}
      sx={{
        direction: 'rtl',
        flex: '1 1 260px',
        minWidth: 0,
        maxWidth: 420,
        '& .MuiInputBase-root': TEXT_SX,
      }}
    />
  )
}

const hebrewOrder = new Intl.Collator('he')

const TreeInput = ({
  accountId,
  groupIds,
  onChange,
}: {
  accountId: string
  groupIds: string[]
  onChange: (input: SourceInput) => void
}) => {
  const [search, setSearch] = useState('')
  const load = useCallback(() => getWaterTreeZonesList(accountId), [accountId])
  const trees = useOptions<WaterTreeZoneListItem>(load)
  const items = useMemo(
    () =>
      trees.status === 'ready'
        ? [...trees.items].sort((a, b) => hebrewOrder.compare(a.name, b.name))
        : [],
    [trees]
  )
  // A prefilled id waits for its tree, so the picker never holds an unknown value.
  const chosen = groupIds.flatMap(id => {
    const tree = items.find(item => String(item.id) === id)
    return tree ? [tree] : []
  })
  return (
    <FormControl
      size="small"
      data-testid="detailed-readings-tree"
      sx={{ direction: 'rtl', flex: '1 1 260px', minWidth: 0, maxWidth: 420 }}
    >
      <Autocomplete
        multiple
        disableCloseOnSelect
        size="small"
        options={items}
        value={chosen}
        // MUI empties the text on every pick and on blur; only typing or the clear button may change it.
        inputValue={search}
        onInputChange={(_, text, reason) => {
          if (reason === 'input' || reason === 'clear') setSearch(text)
        }}
        getOptionLabel={tree => tree.name}
        isOptionEqualToValue={(a, b) => a.id === b.id}
        onChange={(_, next) =>
          onChange({
            source: 'waterTree',
            groupIds: next.map(t => String(t.id)),
          })
        }
        noOptionsText="לא נמצאו עצים"
        slotProps={{
          listbox: { dir: 'rtl' },
          paper: { dir: 'rtl' },
        }}
        renderOption={({ key, ...props }, tree, { selected }) => (
          <li key={key} {...props} dir="auto">
            <Checkbox size="small" checked={selected} sx={{ p: 0.5, ml: 1 }} />
            {tree.name}
          </li>
        )}
        renderInput={params => (
          <TextField
            {...params}
            label="בחירת עצי מים"
            inputProps={{ ...params.inputProps, dir: 'rtl' }}
          />
        )}
      />
      {trees.status === 'failed' && (
        <FormHelperText error>טעינת רשימת העצים נכשלה</FormHelperText>
      )}
    </FormControl>
  )
}

const UsageTypesInput = ({
  accountId,
  usageTypeIds,
  onChange,
}: {
  accountId: string
  usageTypeIds: string[]
  onChange: (input: SourceInput) => void
}) => {
  const { langKey } = useAppTheme()
  const load = useCallback(
    () => getFilterOptionsWithNames(accountId, 'usage_type_id', langKey),
    [accountId, langKey]
  )
  const usageTypes = useOptions<FilterOption>(load)
  const nameOf = new Map(
    (usageTypes.status === 'ready' ? usageTypes.items : []).map(option => [
      option.value,
      option.name,
    ])
  )
  return (
    <FormControl
      size="small"
      data-testid="detailed-readings-usage-types"
      sx={{ direction: 'rtl', flex: '1 1 220px', minWidth: 0, maxWidth: 360 }}
    >
      <InputLabel id="detailed-readings-usage-types-label">
        בחירת סוגי שימוש
      </InputLabel>
      <Select
        multiple
        labelId="detailed-readings-usage-types-label"
        label="בחירת סוגי שימוש"
        value={usageTypeIds}
        renderValue={ids =>
          ids.map(id => formatCodeName(id, nameOf.get(id))).join(', ')
        }
        onChange={e => {
          const { value } = e.target
          onChange({
            source: 'usageTypes',
            usageTypeIds: typeof value === 'string' ? value.split(',') : value,
          })
        }}
      >
        {(usageTypes.status === 'ready' ? usageTypes.items : []).map(option => (
          <MenuItem key={option.value} value={option.value} dir="auto">
            {formatCodeName(option.value, option.name)}
          </MenuItem>
        ))}
      </Select>
      {usageTypes.status === 'failed' && (
        <FormHelperText error>טעינת סוגי השימוש נכשלה</FormHelperText>
      )}
    </FormControl>
  )
}

const SourceField = ({
  accountId,
  input,
  onChange,
}: {
  accountId: string
  input: SourceInput
  onChange: (input: SourceInput) => void
}) => {
  switch (input.source) {
    case 'waterCards':
    case 'meterNumbers':
      return <ListInput input={input} onChange={onChange} />
    case 'waterTree':
      return (
        <TreeInput
          accountId={accountId}
          groupIds={input.groupIds}
          onChange={onChange}
        />
      )
    case 'usageTypes':
      return (
        <UsageTypesInput
          accountId={accountId}
          usageTypeIds={input.usageTypeIds}
          onChange={onChange}
        />
      )
  }
}

const DetailedReadingsReport = ({
  ref,
}: {
  ref?: Ref<DetailedReadingsHandle>
}) => {
  const { siteId = '' } = useParams()
  const [searchParams] = useSearchParams()
  const { langKey } = useAppTheme()
  const [form, setForm] = useState<DetailedReadingsForm>(() =>
    formFromUrl(searchParams)
  )
  const [report, setReport] = useState<ReportState>({ status: 'idle' })
  const [rangeError, setRangeError] = useState(false)
  const [exporting, setExporting] = useState<ExportState>({ status: 'idle' })
  const latestLoad = useRef(0)

  const search = searchOf(form)
  const query = queryOf(report)
  const shown = shownOf(report)

  const load = async (next: Query) => {
    const loadId = ++latestLoad.current
    setReport(current => ({
      status: 'loading',
      query: next,
      shown: shownOf(current),
    }))
    const result = await getDetailedReadings(next.request, {
      page: next.page + 1,
      size: next.size,
    })
    if (loadId !== latestLoad.current) return
    setReport(
      result.ok
        ? { status: 'loaded', query: next, data: result.data }
        : { status: 'error', message: result.error.message }
    )
  }

  const handleSearch = () => {
    if (!search) return
    const { sortBy, sortOrder } = query?.request ?? DEFAULT_SORT
    setExporting({ status: 'idle' })
    load({
      request: { accountId: siteId, langKey, ...search, sortBy, sortOrder },
      page: 0,
      size: query?.size ?? PAGE_SIZE,
    })
  }

  const handlePageChange = (page: number, size: number) => {
    if (query) load({ ...query, page, size })
  }

  const handleSortChange = (sortBy: string, sortOrder: 'ASC' | 'DESC') => {
    if (query)
      load({
        ...query,
        request: { ...query.request, sortBy, sortOrder },
        page: 0,
      })
  }

  useImperativeHandle(ref, () => ({
    exportExcel: async () => {
      if (!query) return
      setExporting({ status: 'running' })
      const result = await exportDetailedReadings(query.request)
      setExporting(
        result.ok
          ? { status: 'idle' }
          : { status: 'failed', message: result.error.message }
      )
    },
  }))

  const shownSortBy = query?.request.sortBy
  const shownSortOrder = query?.request.sortOrder
  const sortModel = useMemo<GridSortModel>(
    () =>
      shownSortBy
        ? [
            {
              field: shownSortBy,
              sort: shownSortOrder === 'ASC' ? 'asc' : 'desc',
            },
          ]
        : [],
    [shownSortBy, shownSortOrder]
  )

  const setRange = (range: DateRangeValue) =>
    setForm(current => ({
      ...current,
      from: formatDay(range.from),
      to: formatDay(range.to),
    }))

  const setInput = (input: SourceInput) =>
    setForm(current => ({ ...current, input }))

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'flex-start',
          gap: 2,
          mb: 2,
        }}
      >
        <Box sx={{ flex: '1 1 280px', minWidth: 0, maxWidth: 360 }}>
          <AppDateRangePicker
            fromLabel="ת.התחלה"
            toLabel="ת.סיום"
            value={{ from: parseDay(form.from), to: parseDay(form.to) }}
            onChange={setRange}
            onErrorChange={setRangeError}
            disableFuture
            clearable
          />
          {datesReversed(form) && (
            <FormHelperText error>
              תאריך ההתחלה מאוחר מתאריך הסיום
            </FormHelperText>
          )}
        </Box>
        <RadioGroup
          row
          aria-label="מקור הקריאות"
          data-testid="detailed-readings-source"
          value={form.input.source}
          onChange={e => {
            if (isSource(e.target.value)) setInput(inputFor(e.target.value))
          }}
        >
          {Object.entries(SOURCE_LABELS).map(([source, label]) => (
            <FormControlLabel
              key={source}
              value={source}
              control={<Radio size="small" />}
              label={<Typography sx={TEXT_SX}>{label}</Typography>}
            />
          ))}
        </RadioGroup>
        <SourceField
          accountId={siteId}
          input={form.input}
          onChange={setInput}
        />
        <Button
          onClick={handleSearch}
          variant="outlined"
          disabled={!search || rangeError}
          data-testid="detailed-readings-search"
          sx={searchButtonSx}
        >
          חיפוש
        </Button>
      </Box>

      {(report.status === 'loading' || exporting.status === 'running') && (
        <LinearProgress sx={{ width: '95%', borderRadius: 1, mb: 1 }} />
      )}
      {exporting.status === 'failed' && (
        <Alert
          severity="error"
          onClose={() => setExporting({ status: 'idle' })}
          sx={{ width: '95%', mb: 1 }}
        >
          {exporting.message}
        </Alert>
      )}

      {report.status === 'error' && (
        <Alert severity="error" sx={{ width: '95%' }}>
          {report.message}
        </Alert>
      )}

      {query && (
        <Box sx={{ width: '95%', height: '75%' }}>
          <DataGridPremiumDemo
            columns={detailedReadingsColumns}
            rows={shown?.rows ?? []}
            hideFooter
            headerPosition="bottom"
            width="100%"
            pageSize={query.size}
            serverSideRowCount={shown?.total ?? 0}
            serverSidePage={query.page}
            onPaginationChange={handlePageChange}
            paginationMode="server"
            sortingMode="server"
            onSortChange={handleSortChange}
            externalSortModel={sortModel}
            loading={report.status === 'loading'}
            noRowsLabel="לא נמצאו קריאות"
            columnSettingsKey={COLUMN_SETTINGS_KEY}
          />
        </Box>
      )}

      {report.status === 'idle' && (
        <Box
          sx={{
            width: '100%',
            height: '50vh',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            flexDirection: 'column',
          }}
        >
          <img src={FileIcon} alt="FileIcon" />
          <Typography
            fontWeight={700}
            sx={{ my: 1, fontFamily: 'Rubik, sans-serif' }}
          >
            הפקת דוח
          </Typography>
          <Typography>יש לבחור פרמטרים וללחוץ על חיפוש</Typography>
        </Box>
      )}
    </Box>
  )
}

export default DetailedReadingsReport
